import { useEffect, useMemo, useState } from "react";
import {
  FileSpreadsheet,
  RefreshCw,
  Search,
  Info,
  Users,
  ClipboardList,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, apiErrorMessage, formatRupiah } from "@/lib/api";

// Nama pseudo-item untuk kolom bawaan yang muncul di checklist utama supaya
// admin bisa memilih dengan alur yang sama seperti komponen master.
const BUILTIN = {
  IDENTITAS: "__IDENTITAS_LENGKAP__",
  KEHADIRAN: "__KEHADIRAN__",
  GAJI_POKOK: "__GAJI_POKOK__",
  TOTAL_BRUTO: "__TOTAL_BRUTO__",
  PPH21_DIPOTONG: "__PPH21_DIPOTONG__",
  TOTAL_POTONGAN: "__TOTAL_POTONGAN__",
  NETTO: "__NETTO__",
  TOTAL_BEBAN: "__TOTAL_BEBAN__",
  TOTAL_BIAYA_PRSH: "__TOTAL_BIAYA_PRSH__",
};

export function ExportXlsxCustomDialog({ open, onOpenChange, run, unitId }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [jumlahSlip, setJumlahSlip] = useState(0);
  const [totalGajiPokok, setTotalGajiPokok] = useState(0);
  const [totalPph21, setTotalPph21] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);
  const [search, setSearch] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  function semuaKolom(list) {
    const s = new Set([
      BUILTIN.IDENTITAS,
      BUILTIN.KEHADIRAN,
      BUILTIN.GAJI_POKOK,
      BUILTIN.TOTAL_BRUTO,
      BUILTIN.PPH21_DIPOTONG,
      BUILTIN.TOTAL_POTONGAN,
      BUILTIN.NETTO,
      BUILTIN.TOTAL_BEBAN,
      BUILTIN.TOTAL_BIAYA_PRSH,
    ]);
    for (const it of list) s.add(it.nama);
    return s;
  }

  async function fetchData({ resetSelection = false } = {}) {
    if (!run) return;
    setLoading(true);
    try {
      const params = { run_id: run.id };
      if (unitId) params.unit_id = unitId;
      const { data } = await api.get("/reports/payroll/komponen-tersedia", { params });
      const list = data.items || [];
      setItems(list);
      setJumlahSlip(data.jumlah_slip || 0);
      setTotalGajiPokok(data.total_gaji_pokok || 0);
      setTotalPph21(data.total_pph21_dipotong || 0);
      if (resetSelection) setSelected(semuaKolom(list));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    // Default: Semua Kolom (hasilnya identik dengan angka resmi slip)
    fetchData({ resetSelection: true });
    setSearch("");
    setShowAdvanced(false);
    // eslint-disable-next-line
  }, [open, run?.id]);

  function toggle(name) {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(name)) nx.delete(name);
      else nx.add(name);
      return nx;
    });
  }

  function togglePreset(preset) {
    if (preset === "clear") {
      setSelected(new Set([BUILTIN.IDENTITAS])); // identitas inti + jabatan/bank
      return;
    }
    if (preset === "lengkap") {
      setSelected(semuaKolom(items));
    }
  }

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.nama.toLowerCase().includes(q));
  }, [items, search]);

  // Estimasi baris TOTAL persis mengikuti rumus backend: hanya kolom terpilih.
  const estimasi = useMemo(() => {
    let pend = selected.has(BUILTIN.GAJI_POKOK) ? totalGajiPokok : 0;
    let pot = selected.has(BUILTIN.PPH21_DIPOTONG) ? totalPph21 : 0;
    let beban = 0;
    for (const it of items) {
      if (!selected.has(it.nama)) continue;
      if (it.tipe === "Potongan") pot += it.total || 0;
      else if (it.tipe === "Beban Perusahaan") beban += it.total || 0;
      else pend += it.total || 0;
    }
    return { pend, pot, netto: pend - pot, beban };
  }, [items, selected, totalGajiPokok, totalPph21]);

  const jumlahKolomTerpilih = useMemo(() => {
    // Estimasi jumlah kolom output: 3 (identitas inti) + optional
    let n = 3;
    if (selected.has(BUILTIN.IDENTITAS)) n += 5; // Jabatan/Bank/NoRek/NPWP/PTKP
    if (selected.has(BUILTIN.KEHADIRAN)) n += 5;
    if (selected.has(BUILTIN.GAJI_POKOK)) n += 1;
    // Komponen individual
    for (const it of items) if (selected.has(it.nama)) n += 1;
    if (selected.has(BUILTIN.TOTAL_BRUTO)) n += 1;
    if (selected.has(BUILTIN.PPH21_DIPOTONG)) n += 1;
    if (selected.has(BUILTIN.TOTAL_POTONGAN)) n += 1;
    if (selected.has(BUILTIN.NETTO)) n += 1;
    if (selected.has(BUILTIN.TOTAL_BEBAN)) n += 1;
    if (selected.has(BUILTIN.TOTAL_BIAYA_PRSH)) n += 1;
    return n;
  }, [items, selected]);

  async function unduh() {
    // Pastikan ada minimal satu kolom di luar identitas
    const komponenPicked = items.filter((it) => selected.has(it.nama)).map((it) => it.nama);
    const anyMoney =
      selected.has(BUILTIN.GAJI_POKOK) ||
      selected.has(BUILTIN.TOTAL_BRUTO) ||
      selected.has(BUILTIN.PPH21_DIPOTONG) ||
      selected.has(BUILTIN.TOTAL_POTONGAN) ||
      selected.has(BUILTIN.NETTO) ||
      selected.has(BUILTIN.TOTAL_BEBAN) ||
      selected.has(BUILTIN.TOTAL_BIAYA_PRSH) ||
      selected.has(BUILTIN.KEHADIRAN) ||
      komponenPicked.length > 0;
    if (!anyMoney) {
      toast.error("Pilih minimal satu kolom komponen atau total untuk diekspor");
      return;
    }
    setDownloading(true);
    try {
      const body = {
        run_id: run.id,
        include_identitas_lengkap: selected.has(BUILTIN.IDENTITAS),
        include_kehadiran: selected.has(BUILTIN.KEHADIRAN),
        include_gaji_pokok: selected.has(BUILTIN.GAJI_POKOK),
        komponen_names: komponenPicked,
        include_pph21_dipotong: selected.has(BUILTIN.PPH21_DIPOTONG),
        include_total_bruto: selected.has(BUILTIN.TOTAL_BRUTO),
        include_total_potongan: selected.has(BUILTIN.TOTAL_POTONGAN),
        include_netto: selected.has(BUILTIN.NETTO),
        include_total_beban_perusahaan: selected.has(BUILTIN.TOTAL_BEBAN),
        include_total_biaya_perusahaan: selected.has(BUILTIN.TOTAL_BIAYA_PRSH),
      };
      if (unitId) body.unit_id = unitId;
      const resp = await api.post("/reports/payroll/export-xlsx-custom", body, {
        responseType: "blob",
      });
      const totalKolom = resp.headers?.["x-total-kolom"] || "?";
      const totalBaris = resp.headers?.["x-total-baris"] || "?";
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RekapGaji_${run.periode}_kustom.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Excel diunduh — ${totalBaris} pegawai · ${totalKolom} kolom`);
      onOpenChange(false);
    } catch (e) {
      let msg = apiErrorMessage(e);
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j.detail || msg;
        } catch {
          /* ignore */
        }
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] flex flex-col"
        data-testid="xlsx-custom-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-display flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-teal-700" />
            Export Excel — Pilih Kolom
          </DialogTitle>
          <p className="text-xs text-slate-500">
            Pilih kolom yang ingin diikutkan. Kolom total (Pendapatan, Potongan, Gaji Bersih,
            Beban, Biaya) dan baris <b>TOTAL</b> dihitung <i>hanya</i> dari kolom yang Anda
            centang — komponen yang tidak dicentang diabaikan sepenuhnya.
            Periode: <b>{run?.periode_label}</b> · {jumlahSlip} pegawai.
          </p>
        </DialogHeader>

        {/* Preset buttons */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 shrink-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
            Preset Cepat
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => togglePreset("lengkap")}
              className="h-7 text-[11px]"
              data-testid="xlsx-preset-lengkap"
            >
              Semua Kolom
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => togglePreset("clear")}
              className="h-7 text-[11px] text-slate-500"
              data-testid="xlsx-preset-clear"
            >
              Kosongkan
            </Button>
          </div>
        </div>

        {/* Bawaan (identitas + kehadiran) */}
        <div className="rounded-lg border border-slate-200 bg-white shrink-0">
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <Users size={12} className="text-teal-700" />
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Kolom Informasi Pegawai
            </div>
          </div>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <BawaanRow
              testId="xlsx-opt-identitas"
              checked={selected.has(BUILTIN.IDENTITAS)}
              onToggle={() => toggle(BUILTIN.IDENTITAS)}
              label="Identitas Lengkap"
              hint="Jabatan, Bank, No. Rekening, NPWP, PTKP"
            />
            <BawaanRow
              testId="xlsx-opt-kehadiran"
              checked={selected.has(BUILTIN.KEHADIRAN)}
              onToggle={() => toggle(BUILTIN.KEHADIRAN)}
              label="Kehadiran & Lembur"
              hint="Hari Kerja, Hadir, Terlambat, Alpha, Jam Lembur"
            />
          </div>
          <div className="px-3 pb-2 text-[10px] text-slate-400 italic">
            NPP, Nama, dan Unit Kerja selalu diikutkan sebagai kolom inti.
          </div>
        </div>

        {/* Komponen list */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50 shrink-0">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
              <ClipboardList size={12} /> Kolom Komponen Gaji
            </div>
            <div className="text-[10px] text-slate-500">
              Terpilih: <b className="text-teal-800">{jumlahKolomTerpilih}</b> kolom
            </div>
          </div>

          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 shrink-0">
            <Search size={13} className="text-slate-400" />
            <input
              className="flex-1 text-sm outline-none bg-transparent"
              placeholder="Cari nama komponen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="xlsx-input-search"
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="xlsx-komponen-scroll">
            <div className="divide-y divide-slate-100">
              {/* Gaji Pokok */}
              <ItemRow
                testId="xlsx-opt-gaji-pokok"
                checked={selected.has(BUILTIN.GAJI_POKOK)}
                onToggle={() => toggle(BUILTIN.GAJI_POKOK)}
                title="Gaji Pokok"
                tag="Pendapatan"
                tagTone="emerald"
                hint={
                  <>
                    Total periode: <b>{formatRupiah(totalGajiPokok)}</b> · nominal gaji pokok tiap
                    pegawai.
                  </>
                }
              />

              {loading ? (
                <div className="px-3 py-6 text-center text-xs text-slate-500">
                  Memuat daftar komponen…
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-slate-500">
                  Tidak ada komponen aktif di periode ini.
                </div>
              ) : (
                filteredItems.map((it) => {
                  const tone =
                    it.tipe === "Potongan"
                      ? "rose"
                      : it.tipe === "Beban Perusahaan"
                      ? "indigo"
                      : "emerald";
                  return (
                    <ItemRow
                      key={it.nama}
                      testId={`xlsx-opt-${it.nama}`}
                      checked={selected.has(it.nama)}
                      onToggle={() => toggle(it.nama)}
                      title={it.label || it.nama}
                      tag={it.tipe}
                      tagTone={tone}
                      systemAuto={it.system_auto}
                      hint={
                        <>
                          {it.system_auto ? (
                            <span className="text-teal-700">Hasil hitung sistem · </span>
                          ) : null}
                          Total periode: <b>{formatRupiah(it.total)}</b> · terpakai
                          di {it.count} slip
                          {it.tipe === "Potongan" ? (
                            <span className="text-rose-600">
                              {" "}
                              · dijumlah <b>negatif</b> pada baris TOTAL
                            </span>
                          ) : null}
                        </>
                      }
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Advanced: subtotal & totals */}
        <div className="rounded-lg border border-slate-200 bg-white shrink-0">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-100"
            data-testid="xlsx-toggle-advanced"
          >
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Kolom Subtotal & Total
            </span>
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showAdvanced ? (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <BawaanRow
                testId="xlsx-opt-total-bruto"
                checked={selected.has(BUILTIN.TOTAL_BRUTO)}
                onToggle={() => toggle(BUILTIN.TOTAL_BRUTO)}
                label="Total Pendapatan (kolom terpilih)"
                hint="Gaji Pokok + komponen Pendapatan yang dicentang"
              />
              <BawaanRow
                testId="xlsx-opt-pph21-dipotong"
                checked={selected.has(BUILTIN.PPH21_DIPOTONG)}
                onToggle={() => toggle(BUILTIN.PPH21_DIPOTONG)}
                label="PPh 21 (Dipotong)"
                hint={`Total periode: ${formatRupiah(totalPph21)}`}
              />
              <BawaanRow
                testId="xlsx-opt-total-potongan"
                checked={selected.has(BUILTIN.TOTAL_POTONGAN)}
                onToggle={() => toggle(BUILTIN.TOTAL_POTONGAN)}
                label="Total Potongan (kolom terpilih)"
                hint="Komponen Potongan yang dicentang + PPh 21 bila dicentang"
              />
              <BawaanRow
                testId="xlsx-opt-netto"
                checked={selected.has(BUILTIN.NETTO)}
                onToggle={() => toggle(BUILTIN.NETTO)}
                label="Gaji Bersih (kolom terpilih)"
                hint="Total Pendapatan terpilih − Total Potongan terpilih"
              />
              <BawaanRow
                testId="xlsx-opt-total-beban"
                checked={selected.has(BUILTIN.TOTAL_BEBAN)}
                onToggle={() => toggle(BUILTIN.TOTAL_BEBAN)}
                label="Total Beban Perusahaan (kolom terpilih)"
                hint="Komponen Beban Perusahaan yang dicentang"
              />
              <BawaanRow
                testId="xlsx-opt-total-biaya-prsh"
                checked={selected.has(BUILTIN.TOTAL_BIAYA_PRSH)}
                onToggle={() => toggle(BUILTIN.TOTAL_BIAYA_PRSH)}
                label="Total Biaya Perusahaan (kolom terpilih)"
                hint="Total Pendapatan terpilih + Total Beban terpilih"
              />
            </div>
          ) : (
            <div className="px-3 py-2 text-[11px] text-slate-500 flex items-start gap-1.5">
              <Info size={11} className="mt-0.5 shrink-0" />
              Ada 6 kolom subtotal/total (Pendapatan, PPh 21, Potongan, Gaji Bersih, Beban,
              Biaya) — semuanya dihitung dari kolom terpilih. Klik untuk atur.
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="rounded-md border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <b>Estimasi Baris TOTAL (kolom terpilih)</b>
              <div className="text-[11px] text-teal-700/80 mt-0.5">
                Pendapatan <b data-testid="xlsx-total-pendapatan">{formatRupiah(estimasi.pend)}</b>
                {" · "}Potongan <b data-testid="xlsx-total-potongan">{formatRupiah(estimasi.pot)}</b>
                {" · "}Beban Prsh <b data-testid="xlsx-total-beban">{formatRupiah(estimasi.beban)}</b>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-teal-700/80">
                Gaji Bersih terpilih
              </div>
              <div
                className="text-lg font-extrabold text-teal-800 font-mono-clock"
                data-testid="xlsx-total-preview"
              >
                {formatRupiah(estimasi.netto)}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="xlsx-cancel">
            Batal
          </Button>
          <Button
            onClick={() => fetchData()}
            variant="outline"
            className="gap-1.5"
            disabled={loading}
            data-testid="xlsx-refresh"
          >
            <RefreshCw size={13} /> Muat Ulang
          </Button>
          <Button
            onClick={unduh}
            disabled={downloading || loading}
            className="bg-teal-700 hover:bg-teal-800 gap-1.5"
            data-testid="xlsx-unduh"
          >
            <FileSpreadsheet size={13} />{" "}
            {downloading ? "Mengunduh…" : "Unduh Excel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BawaanRow({ testId, checked, onToggle, label, hint }) {
  return (
    <label
      className="flex items-start gap-3 px-2 py-2 rounded-md hover:bg-slate-50 cursor-pointer"
      data-testid={testId}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {hint ? <div className="text-[11px] text-slate-500">{hint}</div> : null}
      </div>
    </label>
  );
}

function ItemRow({ testId, checked, onToggle, title, tag, tagTone, hint, systemAuto }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    rose: "bg-rose-50 text-rose-700 border border-rose-100",
    indigo: "bg-indigo-50 text-indigo-700 border border-indigo-100",
  };
  return (
    <label
      className="flex items-start gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
      data-testid={testId}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          {title}
          {tag ? (
            <span
              className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                tones[tagTone] || tones.emerald
              }`}
            >
              {tag}
            </span>
          ) : null}
          {systemAuto ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider bg-teal-600 text-white">
              Sistem
            </span>
          ) : null}
        </div>
        {hint ? <div className="text-[11px] text-slate-500">{hint}</div> : null}
      </div>
    </label>
  );
}

export default ExportXlsxCustomDialog;
